import {Component, Input, OnInit} from '@angular/core';
import { ReactiveFormsModule, UntypedFormBuilder, UntypedFormGroup } from '@angular/forms';
import {Store} from '@ngxs/store';
import {
  ConfigurationState,
  ContactDto,
  IProvince,
  IState,
  LaunchDarklyService,
  OrganizationService,
  PostingDataResponseModel,
  StatesProvincesService,
  StorageLocation,
  StorageLocationAddress,
  StorageLocationContact,
  StorageLocationForForm,
  StorageLocationService,
  TransportationHubService,
  EditPostingState,
  SetLocationNumber,
  SetSelectedIntransitForLoc,
  SetSelectedStorageLocationRadioButton,
  SetVisitedStoreLocation,
  ToggleSpinner,
  UpdatePosting,
  DateUtilsService,
  EditPostingFormNames,
  PostingTypes,
  DateTimeFormat,
  DateFormatterService
} from 'remarketing-angular-library';
import {StorageLocationInterface} from 'remarketing-angular-library/interfaces/dtos/storage-location.dto';
import {
  NgbCalendar,
  NgbDate,
  NgbDateParserFormatter,
  NgbDatepickerConfig,
  NgbDateStruct,
  NgbInputDatepicker
} from '@ng-bootstrap/ng-bootstrap';
import {cloneDeep} from 'lodash';
import {take, takeUntil} from 'rxjs/operators';
import {PostingFormDirective} from '../posting-form/posting-form.directive';
import { TranslocoDirective, TranslocoService } from '@jsverse/transloco';
import {PostingFormTrackingService} from '../../services/posting-form-tracking.service';
import {PostingAnalyticsHelperService} from '../../services/posting-analytics-helper.service';
import { EditPostingUtilsService } from '../../services/edit-posting-utils.service';
import { CommonModule } from '@angular/common';
import { FormatPhoneNumberPipe } from 'smartauction-frontend-micro-library';

@Component({
    selector: 'app-location-form',
    templateUrl: './location-form.component.html',
    styleUrls: ['./location-form.component.scss'],
    providers: [{ provide: NgbDateParserFormatter, useClass: DateFormatterService }],
    imports: [TranslocoDirective, ReactiveFormsModule, NgbInputDatepicker, CommonModule, FormatPhoneNumberPipe]
})
export class LocationFormComponent extends PostingFormDirective implements OnInit {
  postingData: PostingDataResponseModel;
  locationForm: UntypedFormGroup;
  stateProvinceList;
  storageLocations: StorageLocationForForm[];
  selectedStorageLocation: StorageLocationForForm;
  locationNumberForm: StorageLocationForForm;
  locationRetrievalFailed = false;
  editableAddress = true;
  isAddressValidated = null;
  errorCount = 0;
  validLocationName = true;
  validAddress = true;
  validCity = true;
  validState = true;
  validPostalCode = true;
  inTransitArrivalDate: Date;
  selectedIntransit = false;
  postAdhocLocation = false;
  inTransitEligible = false;
  minDate: NgbDateStruct;
  retrieveclicked = false;
  selectedIntransitForLoc = false;
  originalPostingData: PostingDataResponseModel;
  textSelect: string;
  isLocationUuidPresent: boolean;
  postingType: PostingTypes;
  @Input() postingTypeString: string;
  inTransitDateValid = true;

  get isCanadianTenant() {
    return this.launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  get countryBasedOnTenant() {
    return this.isCanadianTenant ? 'CAN' : 'USA';
  }

  constructor(
    private readonly formBuilder: UntypedFormBuilder,
    private readonly organizationService: OrganizationService,
    private readonly store: Store,
    private readonly storageLocationService: StorageLocationService,
    private readonly transportationHubService: TransportationHubService,
    private readonly config: NgbDatepickerConfig,
    private readonly dateUtilService: DateUtilsService,
    private readonly launchDarklyService: LaunchDarklyService,
    private readonly statesProvincesService: StatesProvincesService,
    private readonly translocoService: TranslocoService,
    private readonly editPostingUtilsService: EditPostingUtilsService,
    private readonly ngbCalendar: NgbCalendar,
    private readonly postingFormTrackingService: PostingFormTrackingService,
    private readonly postingAnalyticsHelperService: PostingAnalyticsHelperService,
    private readonly dateFormatter: DateFormatterService
  ) {
    super();
    const current = new Date();
    config.minDate = {
      year: current.getFullYear(),
      month: current.getMonth() + 1,
      day: current.getDate()
    };
    config.outsideDays = 'hidden';
  }

  ngOnInit() {
    this.store
      .select(EditPostingState.updatedPosting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(postingData => {
        this.postingData = cloneDeep(postingData);
      });
    this.store
      .select(EditPostingState.originalPosting)
      .pipe(takeUntil(this.destroyed$))
      .subscribe(originalPostingData => {
        this.originalPostingData = cloneDeep(originalPostingData);
      });
    const isAddressChanged =
      JSON.stringify(this.postingData.inventoryItem?.storageLocation?.address) ===
      JSON.stringify(this.originalPostingData.inventoryItem?.storageLocation?.address);
    this.retrieveclicked = this.store.selectSnapshot(EditPostingState.visitedStoreLocation);
    this.selectedIntransitForLoc = this.store.selectSnapshot(EditPostingState.selectedIntransitForLoc);
    this.postingType = this.store.selectSnapshot(EditPostingState.postingType);
    this.locationForm = this.formBuilder.group({
      radioSelection: [
        this.store.selectSnapshot(EditPostingState.selectedStorageLocationRadioButton)
          ? this.store.selectSnapshot(EditPostingState.selectedStorageLocationRadioButton)
          : 'storageLocation'
      ],
      locationNumber: [this.store.selectSnapshot(EditPostingState.locationNumber)],
      storageLocation: [],
      locationName: [isAddressChanged ? '' : this.postingData.inventoryItem.storageLocation.name],
      addressLine1: [isAddressChanged ? '' : this.postingData.inventoryItem.storageLocation.address.addressLine1],
      addressLine2: [isAddressChanged ? '' : this.postingData.inventoryItem.storageLocation.address.addressLine2],
      city: [isAddressChanged ? '' : this.postingData.inventoryItem.storageLocation.address.city],
      postalCode: [isAddressChanged ? '' : this.postingData.inventoryItem.storageLocation.address.postalCode],
      contactPhone: [isAddressChanged ? '' : this.postingData.inventoryItem.storageLocation.contactPhone],
      inTransitArrivalDate: [
        this.dateUtilService.getDateForDatePicker(
          new Date(this.postingData.inventoryItem.postingInformation.intransitArrivalDate)
        )
      ],
      state: [isAddressChanged ? '' : this.postingData.inventoryItem.storageLocation.address.state]
    });

    this.getTranslations();
    this.stateProvinceList = this.statesProvincesList;
    this.getAndPopulateStorageLocationDisplayData();

    this.locationRetrievalFailed = false;
    this.store
      .select(_state => _state.org)
      .subscribe(org => {
        this.postAdhocLocation = org.organization.organizationProfile.postAdhocLocation;
        this.inTransitEligible = org.organization.organizationProfile.inTransitEligible;
      });

    //Called for form tracking on reload
    this.postingFormTrackingService.syncFormToInvalidValidationFormsOnLoad(
      this.postingType,
      EditPostingFormNames.INFO_LOCATION
    );

    this.ifNewPostingCheckForLocationUuidAndHandleFormTracking();

    if (
      this.postingType !== PostingTypes.NEW &&
      this.postingDate !== null &&
      this.originalInTransitArrivalDate !== null
    ) {
      this.config.minDate = {
        year: this.postingDate.year,
        month: this.postingDate.month,
        day: this.postingDate.day
      };
    }
    this.sendPageloadAnalyticsEvent();
  }

  sendPageloadAnalyticsEvent() {
    this.postingAnalyticsHelperService.sendPageloadAnalyticsEvent(
      this.store.selectSnapshot(ConfigurationState.configuration).tenantName,
      this.postingTypeString,
      EditPostingFormNames.INFO_LOCATION
    );
  }

  getTranslations() {
    this.translocoService
      .selectTranslateObject('sellModule.posting')
      .pipe(takeUntil(this.destroyed$))
      .subscribe(translatedObj => {
        this.textSelect = translatedObj.damagesForm.textSelect;
      });
  }

  getAndPopulateStorageLocationDisplayData(): void {
    const sellerOeUuid = this.store.selectSnapshot(EditPostingState.selectedSellerOeUuid);
    this.storageLocationService
      .getStorageLocationData(this.editPostingUtilsService.getSelectedDealershipUuid(sellerOeUuid))
      .pipe(take(1))
      .subscribe(response => {
        if (response?.length > 0) {
          this.populateLocationDisplayDataWithStorageLocationServiceResponse(response);
        } else if (this.currentLocationIsPresent()) {
          this.populateLocationDisplayDataWithCurrentLocationOnly();
        }
      });
  }

  public currentLocationIsPresent() {
    const storageLocation = this.postingData?.inventoryItem?.storageLocation;
    return storageLocation ? this.storageLocationNameAndAddressArePresent(storageLocation) : false;
  }

  private storageLocationNameAndAddressArePresent(storageLocation: StorageLocation): boolean {
    return !!storageLocation?.address && !!storageLocation?.name;
  }

  public populateLocationDisplayDataWithStorageLocationServiceResponse(response: StorageLocationForForm[]) {
    // alphabetize location list
    response.sort((location1, location2) => (location1.name?.toLowerCase() > location2.name?.toLowerCase() ? 1 : -1));

    // Check for a match between response locations and the current location listed in posting data
    const storageLocation = response.find(
      location => location.locationUuid === this.postingData.inventoryItem?.storageLocation?.locationUuid
    );

    if (storageLocation) {
      // When a match of current location exists in response data, populate display data
      this.storageLocations = response;
      this.populateLocationDisplayData(storageLocation);
    } else if (storageLocation === undefined && response.length > 0) {
      // When a match with the current location does not exist but other alternative locations are returned in the response,
      // add the current location to the response (for dropdown options) and populate display data
      const storageLocationFromPostingData = this.buildStorageLocationForFormFromPostingData();
      response.push(storageLocationFromPostingData);
      this.storageLocations = response;
      this.populateLocationDisplayData(storageLocationFromPostingData);
    }

    // remove undefined storage locations && auto select if 1 location exists
    if (this.postingType === PostingTypes.NEW) {
      this.storageLocations = this.storageLocations.filter(location => location.address.addressLine1 !== undefined);
    }
    this.ifNewPostingPopulateSingleStorageLocation();
  }

  /* Populate storage location automatically
   */
  public ifNewPostingPopulateSingleStorageLocation() {
    if (
      this.storageLocations.length === 1 &&
      this.isStorageLocationSelected() &&
      this.postingType === PostingTypes.NEW
    ) {
      this.populateLocationDisplayData(this.storageLocations[0]);
      this.changeAddress();
    }
  }

  public populateLocationDisplayData(storageLocation: StorageLocationForForm) {
    this.locationForm.patchValue({
      storageLocation: storageLocation?.name
    });
    this.selectedStorageLocation = storageLocation;
    this.locationNumberForm = this.selectedStorageLocation;
    if (this.selectedStorageLocation) {
      this.selectedIntransit = this.selectedStorageLocation['locationIntransitFlag'];
    }
  }

  public populateLocationDisplayDataWithCurrentLocationOnly() {
    const storageLocationFromExistingPostingData = this.buildStorageLocationForFormFromPostingData();
    this.storageLocations = [storageLocationFromExistingPostingData];
    this.populateLocationDisplayData(storageLocationFromExistingPostingData);
  }

  public buildStorageLocationForFormFromPostingData() {
    const storageLocation = this.postingData?.inventoryItem?.storageLocation;
    const address = storageLocation?.address;
    return {
      address: {
        addressLine1: address?.addressLine1,
        addressLine2: address?.addressLine2,
        city: address?.city,
        state: address?.state,
        postalCode: address?.postalCode
      } as StorageLocationAddress,
      contact: {
        phoneNumber: storageLocation?.contactPhone
      } as StorageLocationContact,
      locationUuid: storageLocation?.locationUuid,
      locationIntransitFlag: false,
      name: storageLocation?.name
    } as StorageLocationForForm;
  }

  public isLocationNumberSelected() {
    return this.locationForm.value.radioSelection === 'locationNumber';
  }

  public isStorageLocationSelected() {
    return this.locationForm.value.radioSelection === 'storageLocation';
  }

  public isLocationAddressSelected() {
    return this.locationForm.value.radioSelection === 'locationAddress';
  }

  public changeAddress() {
    this.selectedStorageLocation = this.storageLocations.filter(
      sl => sl.name === this.locationForm.get('storageLocation').value
    )[0];
    this.locationNumberForm = this.selectedStorageLocation;
    this.selectedIntransit = this.selectedStorageLocation['locationIntransitFlag'];
    const radioButton = this.locationForm.get('radioSelection').value;
    this.store.dispatch(new SetSelectedStorageLocationRadioButton(radioButton));

    this.postingData.inventoryItem.storageLocation = {
      address: this.selectedStorageLocation.address,
      locationUuid: this.selectedStorageLocation.locationUuid,
      name: this.selectedStorageLocation.name
    };

    if (!this.showIntransitDate()) {
      this.setIntransitDateToNull();
    }

    this.store.dispatch(new UpdatePosting(this.postingData));
    this.ifNewPostingCheckForLocationUuidAndHandleFormTracking();

    const lengthOfPhone = this.selectedStorageLocation.contact.phoneNumber?.length;

    this.selectedStorageLocation.contact.phoneNumber =
      lengthOfPhone > 0
        ? `${this.selectedStorageLocation.contact.phoneNumber.substring(
            0,
            3
          )}-${this.selectedStorageLocation.contact.phoneNumber.substring(3, lengthOfPhone)}`
        : null;
  }

  public trackFormChanges() {
    this.ifNewPostingSetDefaultStorageLocationObjectsOnPostingData();
    this.postingData.inventoryItem.storageLocation.address.city = this.locationForm.get('city').value;
    this.postingData.inventoryItem.storageLocation.name = this.locationForm.get('locationName').value;
    this.postingData.inventoryItem.storageLocation.address.addressLine2 = this.locationForm.get('addressLine2').value;
    this.postingData.inventoryItem.storageLocation.address.addressLine1 = this.locationForm.get('addressLine1').value;
    this.postingData.inventoryItem.storageLocation.address.state = this.locationForm.get('state').value;
    this.postingData.inventoryItem.storageLocation.address.postalCode = this.locationForm.get('postalCode').value;
    this.postingData.inventoryItem.storageLocation.contactPhone = this.locationForm.get('contactPhone').value;
    this.store.dispatch(new UpdatePosting(this.postingData));
    this.ifNewPostingCheckForLocationUuidAndHandleFormTracking();
  }

  ifNewPostingSetDefaultStorageLocationObjectsOnPostingData(): void {
    if (this.postingType === PostingTypes.NEW) {
      if (!this.postingData.inventoryItem?.storageLocation) {
        this.postingData.inventoryItem.storageLocation = this.buildEmptyStorageLocation();
      }

      if (!this.postingData.inventoryItem.storageLocation.address) {
        this.postingData.inventoryItem.storageLocation.address = this.buildEmptyStorageLocationAddress();
      }
    }
  }

  ifNewPostingCheckForLocationUuidAndHandleFormTracking(): void {
    if (this.postingType === PostingTypes.NEW) {
      this.checkEditPostingStateForLocationUuid();
      this.editPostingUtilsService.addToValidationForms(this.isLocationUuidPresent, EditPostingFormNames.INFO_LOCATION);
    }
  }

  checkEditPostingStateForLocationUuid(): void {
    if (this.postingData?.inventoryItem?.storageLocation?.locationUuid?.length > 0) {
      this.isLocationUuidPresent = true;
    } else {
      this.isLocationUuidPresent = false;
    }
  }

  public retrieveLocationNumberAddress() {
    this.retrieveclicked = true;
    const locationId = this.locationForm.get('locationNumber').value;
    const sellerOeUuid = this.store.selectSnapshot(EditPostingState.selectedSellerOeUuid);

    if (locationId) {
      this.storageLocationService
        .getStorageLocationById(locationId, this.editPostingUtilsService.getSelectedDealershipUuid(sellerOeUuid))
        .subscribe(
          response => {
            this.locationNumberForm = response;
            this.selectedStorageLocation = this.locationNumberForm;
            this.postingData.inventoryItem.storageLocation.locationUuid = this.selectedStorageLocation.locationUuid;
            this.postingData.inventoryItem.storageLocation.name = this.selectedStorageLocation.name;
            this.postingData.inventoryItem.storageLocation.address = this.selectedStorageLocation.address;
            this.locationRetrievalFailed = false;
            this.selectedIntransit = this.locationNumberForm['locationIntransitFlag'];
            this.store.dispatch(new SetVisitedStoreLocation(this.retrieveclicked));
            this.selectedIntransitForLoc = this.selectedIntransit;
            this.store.dispatch(new SetSelectedIntransitForLoc(this.selectedIntransit));
            this.store.dispatch(new UpdatePosting(this.postingData));
            const lengthOfPhone = this.locationNumberForm.contact.phoneNumber?.length;
            this.locationNumberForm.contact.phoneNumber = `${this.locationNumberForm.contact.phoneNumber.substring(
              0,
              3
            )}-${this.locationNumberForm.contact.phoneNumber.substring(3, lengthOfPhone)}`;
          },
          err => {
            this.locationRetrievalFailed = true;
            this.postingAnalyticsHelperService.trackCustomError('No Location Number Retrieved');
            this.selectedIntransit = false;
            console.log(err);
          },
          () => {
            this.ifNewPostingCheckForLocationUuidAndHandleFormTracking();
          }
        );
    } else {
      this.locationRetrievalFailed = true;
      this.postingAnalyticsHelperService.trackCustomError('No Location Number Retrieved');
    }
  }

  resetValidFormFieldStatuses() {
    this.isAddressValidated = null;
    this.validLocationName = true;
    this.validAddress = true;
    this.validCity = true;
    this.validState = true;
    this.validPostalCode = true;
  }

  isEmptyField(field: string): boolean {
    return this.locationForm.get(field).value === null || this.locationForm.get(field).value.length === 0;
  }

  formValidation(): number {
    let errorCount = 0;
    if (this.isEmptyField('locationName')) {
      this.validLocationName = false;
      errorCount += 1;
    }
    if (this.isEmptyField('addressLine1')) {
      this.validAddress = false;
      errorCount += 1;
    }
    if (this.isEmptyField('city')) {
      this.validCity = false;
      errorCount += 1;
    }
    if (this.locationForm.get('state').value === '--') {
      this.validState = false;
      errorCount += 1;
    }
    if (this.isEmptyField('postalCode')) {
      this.validPostalCode = false;
      errorCount += 1;
    }
    return errorCount;
  }

  validateFormAndAddress() {
    this.resetValidFormFieldStatuses();
    this.errorCount = this.formValidation();
    if (this.errorCount === 0) {
      this.validateAddress();
    } else {
      this.postingAnalyticsHelperService.trackCustomError('Address Fields are invalid');
    }
  }

  validateAddress() {
    this.store.dispatch(new ToggleSpinner());
    this.transportationHubService
      .validateAddress({
        addressLine1: this.locationForm.get('addressLine1').value,
        addressLine2: this.locationForm.get('addressLine2').value,
        city: this.locationForm.get('city').value,
        state: this.locationForm.get('state').value,
        postalCode: this.locationForm.get('postalCode').value,
        country: this.countryBasedOnTenant
      })
      .subscribe(
        address => {
          let countryCodeFromThs = address.country;
          // Must be 2 chars for 2.0 DB. Solution for USA -> US & CAN -> CA
          if (countryCodeFromThs.length === 3) {
            countryCodeFromThs = countryCodeFromThs.substring(0, 2);
          }
          this.isAddressValidated = true;
          const { name, contactPhone } = this.postingData.inventoryItem.storageLocation;
          const sellerOeUuid = this.store.selectSnapshot(EditPostingState.selectedSellerOeUuid);

          this.organizationService
            .createStorageLocation(
              this.setStorageLocationContact(
                {
                  name,
                  uuid: this.editPostingUtilsService.getSelectedDealershipUuid(sellerOeUuid),
                  address: {
                    addressLine1: address.addressLine1,
                    addressLine2: address.addressLine2,
                    city: address.city,
                    state: address.state,
                    postalCode: address.postalCode,
                    countryCode: countryCodeFromThs
                  },
                  geoCoordinates: {
                    latitude: address.latitude,
                    longitude: address.longitude
                  }
                },
                contactPhone
              )
            )
            .subscribe(
              storageLocation => {
                this.setStorageLocation(
                  this.setStorageLocationContactPhone(
                    {
                      name: storageLocation.name,
                      address: storageLocation.address,
                      locationUuid: storageLocation.locationUuid
                    } as StorageLocation,
                    storageLocation.contact || {}
                  )
                );
                this.editableAddress = false;
                this.store.dispatch(new ToggleSpinner());
              },
              error => {
                this.isAddressValidated = false;
                this.postingAnalyticsHelperService.trackCustomError('Invalid Address');
                this.store.dispatch(new ToggleSpinner());
              },
              () => {
                this.ifNewPostingCheckForLocationUuidAndHandleFormTracking();
              }
            );
        },
        error => {
          this.isAddressValidated = false;
          this.postingAnalyticsHelperService.trackCustomError('Invalid Address');
          this.store.dispatch(new ToggleSpinner());
        }
      );
  }

  setStorageLocationContact(storageLocation: StorageLocationInterface, contactPhone: string) {
    if (contactPhone) {
      return {
        ...storageLocation,
        contact: {
          phoneAreaCode: contactPhone.substring(0, 3),
          phoneNumber: contactPhone.substring(3, contactPhone.length)
        }
      };
    }
    return storageLocation;
  }

  setStorageLocationContactPhone(storageLocation: StorageLocation, contact: ContactDto) {
    if (contact.phoneAreaCode && contact.phoneNumber) {
      return { ...storageLocation, contactPhone: `${contact.phoneAreaCode}${contact.phoneNumber}` };
    }
    return storageLocation;
  }

  setStorageLocation(storageLocation: StorageLocation) {
    this.postingData.inventoryItem.storageLocation = {
      ...this.postingData.inventoryItem.storageLocation,
      ...storageLocation
    };
    this.store.dispatch(new UpdatePosting(this.postingData));
  }

  editFormAddress() {
    this.editableAddress = true;
  }

  public resetFlagsForLocationNumber(value) {
    this.locationForm.get('storageLocation').setValue(this.originalPostingData.inventoryItem.storageLocation?.name);
    this.locationForm.get('locationNumber').setValue(null);
    this.selectedIntransitForLoc = false;
    this.store.dispatch(new SetVisitedStoreLocation(false));
    this.locationRetrievalFailed = false;
    this.selectedIntransit = this.locationNumberForm['locationIntransitFlag'];
    const radioButton = this.locationForm.get('radioSelection').value;
    this.store.dispatch(new SetSelectedStorageLocationRadioButton(radioButton));
    this.selectedStorageLocation = this.storageLocations.filter(
      sl => sl.name === this.locationForm.get('storageLocation').value
    )[0];
    this.locationNumberForm = this.selectedStorageLocation;
    this.ifNewPostingClearStorageInformationAndFlags();
    this.store.dispatch(new UpdatePosting(this.postingData));
    this.ifNewPostingCheckForLocationUuidAndHandleFormTracking();
  }

  public onLocationChange(value) {
    this.locationRetrievalFailed = false;
    this.store.dispatch(new SetLocationNumber(value));
    if (
      this.locationForm.get('locationNumber').value === null ||
      this.locationForm.get('locationNumber').value === ''
    ) {
      this.selectedIntransitForLoc = false;
      this.store.dispatch(new SetVisitedStoreLocation(false));
    }
    this.store.dispatch(new UpdatePosting(this.postingData));
  }

  public resetFlagsForStorageLocation(value) {
    const radioButton = this.locationForm.get('radioSelection').value;
    this.store.dispatch(new SetSelectedStorageLocationRadioButton(radioButton));
    this.locationForm.get('storageLocation').setValue(this.originalPostingData.inventoryItem.storageLocation?.name);
    this.postingData.inventoryItem.storageLocation = this.originalPostingData.inventoryItem.storageLocation;
    this.postingData.inventoryItem.postingInformation.intransitArrivalDate = this.originalPostingData.inventoryItem.postingInformation?.intransitArrivalDate;
    this.selectedIntransit = this.selectedStorageLocation
      ? this.selectedStorageLocation['locationIntransitFlag']
      : false;
    this.selectedStorageLocation = this.storageLocations.filter(
      sl => sl.name === this.locationForm.get('storageLocation').value
    )[0];

    this.locationNumberForm = this.selectedStorageLocation;
    if (this.showIntransitDate()) {
      this.inTransitDateValid = true;
      this.locationForm.patchValue({
        inTransitArrivalDate: this.dateUtilService.getDateForDatePicker(
          this.postingData.inventoryItem.postingInformation?.intransitArrivalDate
        )
      });
    }

    this.ifNewPostingClearStorageInformationAndFlags();
    this.store.dispatch(new UpdatePosting(this.postingData));
    this.ifNewPostingCheckForLocationUuidAndHandleFormTracking();
    this.ifNewPostingPopulateSingleStorageLocation();
  }

  public resetFlagsForLocationAddress(value) {
    const radioButton = this.locationForm.get('radioSelection').value;
    this.store.dispatch(new SetSelectedStorageLocationRadioButton(radioButton));
    this.locationForm.get('locationName').setValue(null);
    this.locationForm.get('addressLine1').setValue(null);
    this.locationForm.get('addressLine2').setValue(null);
    this.locationForm.get('city').setValue(null);
    this.locationForm.get('state').setValue(null);
    this.locationForm.get('postalCode').setValue(null);
    this.locationForm.get('contactPhone').setValue(null);
    this.ifNewPostingClearStorageInformationAndFlags();
    this.store.dispatch(new UpdatePosting(this.postingData));
    this.ifNewPostingCheckForLocationUuidAndHandleFormTracking();
  }

  public ifNewPostingClearStorageInformationAndFlags() {
    if (this.postingType === PostingTypes.NEW) {
      this.postingData.inventoryItem.storageLocation = this.buildDefaultStorageLocationWithEmptyAddress();
      this.isAddressValidated = undefined;
      this.editableAddress = true;
      this.selectedIntransit = false;
      this.selectedIntransitForLoc = false;
    }
  }

  public buildDefaultStorageLocationWithEmptyAddress(): StorageLocation {
    const storageLocationWithEmptyAddress = this.buildEmptyStorageLocation();
    storageLocationWithEmptyAddress.address = this.buildEmptyStorageLocationAddress();
    return storageLocationWithEmptyAddress;
  }

  buildEmptyStorageLocationAddress(): StorageLocationAddress {
    return {
      addressLine1: undefined,
      addressLine2: undefined,
      city: undefined,
      state: undefined,
      postalCode: undefined
    } as StorageLocationAddress;
  }

  buildEmptyStorageLocation(): StorageLocation {
    return {
      address: undefined,
      contactPhone: undefined,
      locationUuid: undefined,
      name: undefined
    } as StorageLocation;
  }

  public changeDate(value) {
    const formattedValue = this.dateFormatter.format(value);
    const parsedValue = this.dateFormatter.parse(formattedValue);

    const sanitizedValue = NgbDate.from(this.dateFormatter.sanitizeDateInput(parsedValue));
    if (formattedValue != null && value) {
      this.inTransitDateValid = this.validateInTransitArrivalDate(sanitizedValue);
      if (this.inTransitDateValid) {
        this.setInTransitDate(parsedValue);
        this.store.dispatch(new UpdatePosting(this.postingData));
      }
    } else {
      this.inTransitDateValid = true;
      this.setIntransitDateToNull();
      this.store.dispatch(new UpdatePosting(this.postingData));
    }
  }

  setIntransitDateToNull() {
    this.postingData.inventoryItem.postingInformation.intransit = false;
    this.postingData.inventoryItem.postingInformation.intransitArrivalDate = null;
  }

  public showIntransitDate() {
    return (
      this.selectedIntransit && this.inTransitEligible && this.locationForm.value?.radioSelection === 'storageLocation'
    );
  }

  public showIntransitDateForLocation() {
    return (
      this.retrieveclicked &&
      this.selectedIntransitForLoc &&
      this.inTransitEligible &&
      this.locationForm.value.radioSelection === 'locationNumber' &&
      !this.locationRetrievalFailed
    );
  }

  setInTransitDate(date: NgbDateStruct) {
    const today = new Date();
    this.postingData.inventoryItem.postingInformation.intransit = true;
    this.postingData.inventoryItem.postingInformation.intransitArrivalDate = new Date(
      date.year,
      date.month - 1,
      date.day,
      today.getHours(),
      today.getMinutes(),
      today.getSeconds(),
      0
    );
  }

  get canadianTenantEnabled(): boolean {
    return this.launchDarklyService.getFeatureFlag('canadianTenantEnabled', false);
  }

  get statesProvincesList(): IState[] | IProvince[] {
    // start list with
    const statesProvinces = [
      {
        name: this.textSelect,
        abbr: '--'
      }
    ];
    return this.canadianTenantEnabled
      ? statesProvinces.concat(this.statesProvincesService.getCAProvinces())
      : statesProvinces.concat(this.statesProvincesService.getUSStates());
  }

  get addressSelected(): boolean {
    const selectedLocation = this.locationForm.get('storageLocation').value;
    return selectedLocation != null;
  }

  get locationNumberSelected(): boolean {
    return this.locationNumberForm?.address?.addressLine1 !== undefined;
  }

  get postingDate(): NgbDate {
    const postingDate = this.postingData.inventoryItem?.postingInformation.postedDate;
    const formatDatePostingDate = this.dateUtilService.formatDate(postingDate, DateTimeFormat.MM_DD_YY, false);
    const postingDateStruct = new Date(formatDatePostingDate);
    return new NgbDate(postingDateStruct.getFullYear(), postingDateStruct.getMonth() + 1, postingDateStruct.getDate());
  }

  get originalInTransitArrivalDate(): NgbDate {
    const originalInTransitArrivalDate = this.originalPostingData.inventoryItem?.postingInformation
      .intransitArrivalDate;
    const originalInTransitArrivalDateStruct = new Date(originalInTransitArrivalDate);
    if (originalInTransitArrivalDate) {
      return new NgbDate(
        originalInTransitArrivalDateStruct.getFullYear(),
        originalInTransitArrivalDateStruct.getMonth() + 1,
        originalInTransitArrivalDateStruct.getDate()
      );
    } else {
      return null;
    }
  }

  validateInTransitArrivalDate(value): boolean {
    const postingDateToNgbDate = this.postingDate;
    const currentMinDate = new Date();
    const currentMinDatetoNgb = new NgbDate(
      currentMinDate.getFullYear(),
      currentMinDate.getMonth() + 1,
      currentMinDate.getDate() - 1
    );
    const originalInTransitArrivalDate = this.originalPostingData.inventoryItem?.postingInformation
      .intransitArrivalDate;

    if (value && value.year !== null && value.month !== null && value.day !== null) {
      if (originalInTransitArrivalDate != null) {
        return (
          this.ngbCalendar.isValid(value) && (postingDateToNgbDate.before(value) || postingDateToNgbDate.equals(value))
        );
      } else {
        return this.ngbCalendar.isValid(value) && currentMinDatetoNgb.before(value);
      }
    } else {
      return false;
    }
  }
}
